/*
 * One way to put several lines in the cart from a product page.
 *
 * The Rod Sleeve pack, the Sleeve Finder and the "Buy 3, Get 1 Free" row all add more than one
 * item at once, which Horizon's product form cannot do. Each of them sends this event instead:
 *
 *   element.dispatchEvent(new CustomEvent('ra:cart-add', { bubbles: true, detail: { items, source, resolve, reject } }))
 *
 * items is [{ id, quantity, properties? }]. This file posts them as one request, then tells the rest of the
 * theme (cart count, Rebuy drawer) with Horizon's own cart events, dispatched from the element
 * that asked. resolve() gets the cart; reject() gets the error, with .status (422 when Shopify refuses the
 * add, 429 when it is busy) and .description (Shopify's own reason) when the store answered.
 *
 * silent: true (opt in, used by the "Complete your kit" strip) adds without opening Rebuy's Smart
 * Cart; the cart count still updates. Every other caller keeps the default, which opens it.
 *
 * First-add race (brief P0-2, 2026-10-17). A first-time visitor has no cart; Rebuy's /cart/update.js writes and
 * our /cart/add.js could each create one, and the add was lost when Rebuy's cart cookie landed last. So:
 *   1. a cart is made first when there is none (assets/ra-cart-ensure.js);
 *   2. adds run one at a time, and the cart event that wakes Rebuy fires only after the add has landed;
 *   3. /cart.js is read back; any added line that is short is posted again, once;
 *   4. for a few seconds after, a change of cart cookie (a late write that made another cart) is checked the
 *      same way, once the cookie has stopped changing, with a silent cart event so the count and drawer follow.
 * Proved offline in every response order (.debug/round3-brief-fixes.md, P0-2).
 *
 * Review fixes M1 and M2 (2026-10-18, .debug/review-fixes-1018.md):
 *   - once the add has landed, a failed /cart.js read is tried again after 800 ms; if that fails too the add still
 *     counts as done (resolve(null)), the drawer is told once a later read works, and nothing is posted again;
 *   - every request has a time limit (cart set-up 10 s, add 40 s, read 10 s), so each job settles and the buttons
 *     can stay locked until it does. An add that runs out of time may still have landed: the cart is read and shown,
 *     and the caller gets an error with .uncertain = true ("check your cart"), never a plain "try again";
 *   - the listener sets detail.accepted = true, so a caller knows its add is in this queue (and must wait for it)
 *     rather than unheard (nothing sent; safe to unlock).
 *
 * Review fixes (2026-10-18, second pass):
 *   - the time limit covers the answer's body too: a body that never arrives no longer holds the queue. A body that
 *     fails after a 200 is .uncertain (the add landed or may have), never a plain failure;
 *   - a 422 (asked for more than is in stock): Shopify adds what it can, so the cart is read and announced (count and
 *     drawer catch up) and the error carries .cart (the cart now, or null) for the caller to say what is in it;
 *   - offline (navigator.onLine === false): nothing is sent; the error has .offline = true;
 *   - a LureShield single add with no properties gets the Buy More Save More tag (as assets/ra-ls1.js), so kit-strip
 *     and other ra:cart-add adds get the multi-buy discount and do not split from the buy box's tagged line. The
 *     LureShield box itself (source 'ra-ls1') is left as sent, so its "Tag adds" setting still works. Collection-card
 *     quick adds do not come through here (Horizon's product form): assets/product-form.js tags them the same way.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { ensureCart, cartCookie } from '@theme/ra-cart-ensure';

/* global Theme */

const WATCH_MS = 6000;
const SETTLE_MS = 1000;
const ENSURE_MS = 10000;
const POST_MS = 40000;
const READ_MS = 10000;
const REREAD_MS = 800;
let queue = Promise.resolve();

/* LureShield single (product lureshield-fishing-lure-cover, its one variant). No page data names it on collection or kit
   pages, so it is fixed here. Adds of it carry the hidden properties Rebuy's Buy More Save More discount looks for, the
   same as assets/ra-ls1.js (RA_BMSM_PROPS). Update this id if the LureShield product is ever re-created, here and in
   assets/ra-cart-ensure.js (the same id and properties for Horizon's own forms, assets/product-form.js; kept here
   too because the offline tests run this file without its imports). */
const LURESHIELD_SINGLE_VARIANT_ID = 45198381219874;
const RA_BMSM_PROPS = { _source: 'Rebuy', _attribution: 'Rebuy Buy More Save More' };
const tagged = (item) =>
  item && Number(item.id) === LURESHIELD_SINGLE_VARIANT_ID && !item.properties ? { ...item, properties: { ...RA_BMSM_PROPS } } : item;

const wait = (ms) => new Promise((done) => window.setTimeout(done, ms));

/* one time limit for a request, its headers and its body both: fetch has none of its own, and a request (or a body)
   that never answers would hold the queue. race() settles by the deadline even if the abort is not honoured. */
function deadline(ms) {
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = 0;
  const over = new Promise((_, fail) => {
    timer = window.setTimeout(() => {
      if (ctrl) ctrl.abort();
      fail(new Error('timed out'));
    }, ms);
  });
  over.catch(() => {});
  return {
    signal: ctrl ? ctrl.signal : null,
    race: (promise) => Promise.race([promise, over]),
    clear: () => window.clearTimeout(timer),
  };
}

/* fetch with a time limit; with read (e.g. r => r.json()), the body is read inside the same limit */
async function within(ms, url, options = {}, read = null) {
  const limit = deadline(ms);
  try {
    const response = await limit.race(fetch(url, limit.signal ? { ...options, signal: limit.signal } : options));
    return read ? await limit.race(read(response)) : response;
  } finally {
    limit.clear();
  }
}

const uncertain = (cause) => {
  const unsure = new Error('We could not confirm the add. Check your cart before adding again.');
  unsure.uncertain = true;
  unsure.cause = cause;
  return unsure;
};

async function post(items) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    /* offline: nothing is sent, so nothing was added */
    const offline = new Error("You're offline. Nothing was added.");
    offline.offline = true;
    throw offline;
  }
  const limit = deadline(POST_MS);
  let response;
  let added;
  try {
    try {
      response = await limit.race(
        fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
          ...(limit.signal ? { signal: limit.signal } : {}),
        })
      );
    } catch (error) {
      /* no answer: the add may or may not have landed */
      throw uncertain(error);
    }
    try {
      added = await limit.race(response.json());
    } catch (error) {
      /* after a 200 the add landed (or may have): never a plain failure. A 429 (busy) answers HTML, not JSON: keep the
         status either way, so the caller can say what went wrong */
      if (response.ok) throw uncertain(error);
      added = {};
    }
  } finally {
    limit.clear();
  }
  if (!added || typeof added !== 'object') added = {};
  if (!response.ok || added.status) {
    const failed = new Error(added.description || added.message || `Add to cart failed (${response.status})`);
    /* 422: Shopify refused the add (most often a length's stock: what's in the cart plus this add is more than is left) */
    failed.status = Number(added.status) || response.status;
    failed.description = typeof added.description === 'string' ? added.description : '';
    throw failed;
  }
  return Array.isArray(added.items) ? added.items : [added];
}

const readCart = () => within(READ_MS, `${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }, (response) => response.json());

/* after a landed add: one read, once more 800 ms later if it fails, else null (the add still counts) */
const readSettled = () =>
  readCart()
    .catch(() => wait(REREAD_MS).then(readCart))
    .then((cart) => (cart && Array.isArray(cart.items) ? cart : null))
    .catch(() => null);

/**
 * The added lines the cart is short of, as items to post again. `want` maps a line key to the quantity it
 * should have; a re-post never adds more of a variant than was asked for.
 */
function shortOf(want, items, cart) {
  /* summed per key: a line with an automatic discount (the reel's Buy 3, get 1) can come back split into parts
     that share one key (3 paid + 1 free) */
  const have = new Map();
  for (const line of cart.items || []) have.set(line.key, (have.get(line.key) || 0) + Number(line.quantity || 0));
  const out = [];
  for (const [key, { quantity, variant }] of want) {
    const short = quantity - (have.get(key) || 0);
    if (short <= 0) continue;
    const asked = items.find((item) => Number(item.id) === variant);
    if (asked) out.push({ ...asked, quantity: Math.min(short, asked.quantity) });
  }
  return out;
}

function announce(host, items, cart, source, silent) {
  if (!cart) return announceLater(host, items, source, silent);
  const deferred = CartLinesUpdateEvent.createPromise();
  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
      raSilent: Boolean(silent),
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, source, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), didError: false },
  });
}

/*
 * The add landed but the cart could not be read. Tell the theme now (the drawer waits on the promise), with the cart
 * once a later read works; if none does, the promise is rejected: the count keeps its number and the drawer stays shut.
 */
function announceLater(host, items, source, silent) {
  const deferred = CartLinesUpdateEvent.createPromise();
  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
      raSilent: Boolean(silent),
    })
  );
  const tries = [2000, 4000];
  const next = () => {
    if (!tries.length) return deferred.reject(new Error('cart could not be read'));
    wait(tries.shift())
      .then(readCart)
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) throw new Error('no cart');
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), didError: false },
        });
      })
      .catch(next);
  };
  next();
}

/*
 * After the add: if the cart cookie changes (a late write made another cart), wait until it has stopped
 * changing, then check the lines once and post what is missing. Waiting matters: writes sent before the
 * switch can still set the old cookie back, and a re-post made in between would land twice.
 */
function watch(host, want, items, source, from) {
  const until = Date.now() + WATCH_MS;
  let seen = cartCookie();
  let since = Date.now();
  const tick = async () => {
    const now = cartCookie();
    if (now !== seen) {
      seen = now;
      since = Date.now();
    }
    if (seen === from || Date.now() - since < SETTLE_MS) {
      if (Date.now() < until + SETTLE_MS) window.setTimeout(tick, 200);
      return;
    }
    try {
      const missing = shortOf(want, items, await readCart());
      if (!missing.length || cartCookie() !== seen) return;
      await post(missing);
      announce(host, missing, await readCart(), source, true);
    } catch {
      /* the shopper still sees the drawer; nothing more to do */
    }
  };
  window.setTimeout(tick, 200);
}

async function run(host, { items: asked, source, silent }) {
  /* the LureShield buy box (source 'ra-ls1') tags its own adds, or not, by its "Tag adds" setting: left as sent */
  const items = source === 'ra-ls1' ? asked : asked.map(tagged);
  /* ensureCart never rejects and the add goes ahead without a cart either way, so a hung set-up only waits 10 s */
  await Promise.race([ensureCart(), wait(ENSURE_MS)]);
  const sentWith = cartCookie();
  let lines;
  try {
    lines = await post(items);
  } catch (error) {
    if (error && error.uncertain) {
      /* the add timed out: show the cart as Shopify has it (the add may be in it), and never post it again */
      const cart = await readSettled();
      if (cart) announce(host, [], cart, source, silent);
    } else if (error && error.status === 422) {
      /* more than is in stock: Shopify adds what it can. Show the cart as it is now and hand it to the caller, which
         says what is in it rather than "try again" (a retry would add the part that did land a second time) */
      const cart = await readSettled();
      if (cart) announce(host, [], cart, source, silent);
      error.cart = cart;
    }
    throw error;
  }
  const want = new Map();
  for (const line of lines) {
    if (!line || !line.key) continue;
    const had = want.get(line.key);
    want.set(line.key, { quantity: (had ? had.quantity : 0) + (Number(line.quantity) || 0), variant: Number(line.variant_id || line.id) });
  }
  /* review M1: the add has landed, so from here a failed read is never a failure the shopper retries */
  let cart = await readSettled();
  /* Same cart as the add went to: re-post what it is short of now. A different cart means a stray write
     landed meanwhile; watch() handles that once the cookie settles, so nothing is posted twice. */
  const missing = cart && cartCookie() === sentWith ? shortOf(want, items, cart) : [];
  if (missing.length) {
    await Promise.race([ensureCart(), wait(ENSURE_MS)]);
    try {
      await post(missing);
    } catch {
      /* the first add landed; a failed top-up is not the shopper's failure */
    }
    cart = await readSettled();
  }
  announce(host, items, cart, source, silent);
  watch(host, want, items, source, sentWith);
  return cart;
}

document.addEventListener('ra:cart-add', (event) => {
  if (!(event instanceof CustomEvent) || !(event.target instanceof Element)) return;
  const host = event.target;
  const { items = [], source = 'ra-pdp', resolve, reject, silent = false } = event.detail || {};
  if (!items.length) return;
  /* the caller's add is in the queue now: it settles (resolve or reject) once the job ends, and not before */
  if (event.detail && typeof event.detail === 'object') event.detail.accepted = true;

  const job = queue.then(() => run(host, { items, source, silent }));
  queue = job.catch(() => {});
  job.then(
    (cart) => resolve?.(cart),
    (error) => {
      host.dispatchEvent(new CartErrorEvent({ error: String(error?.message || error), code: 'SERVICE_UNAVAILABLE' }));
      reject?.(error);
    }
  );
});
